import { useEffect, useState } from 'react';
import type { CustomerDTO } from '@six-pockets/shared';
import { customersApi } from '@/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/** The picker shows about 5 and scrolls for the rest; a longer list means the search needs narrowing. */
const MAX_RESULTS = 50;

/** Debounced server-side customer search by name or mobile number. */
export function useCustomerSearch(query: string) {
  const q = useDebouncedValue(query.trim(), 250);
  const [results, setResults] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    customersApi
      .list({ q, pageSize: MAX_RESULTS }, controller.signal)
      .then((page) => setResults(page.items))
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Search failed');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q]);

  /** True once the debounced query has caught up and returned nothing. */
  const noMatches = !!q && q === query.trim() && !loading && !error && results.length === 0;

  return { results, loading, error, noMatches, searched: q };
}
